import { useState } from "react";
import {
  AppstoreOutlined, AuditOutlined, BarChartOutlined, BuildOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CreditCardOutlined, ExperimentOutlined, FileTextOutlined, GlobalOutlined, HistoryOutlined,
  HomeOutlined, MenuOutlined, MoonOutlined, ProfileOutlined, SafetyCertificateOutlined, SettingOutlined,
  ShoppingCartOutlined, SolutionOutlined, SunOutlined, TeamOutlined, ToolOutlined
} from "@ant-design/icons";
import { Button, Drawer, Grid, Layout, Menu, Space, Switch } from "antd";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PermissionCode, SystemRole } from "@autoflow/types";
import { useAuth } from "../features/auth/AuthProvider";
import { useAppConfig } from "../shared/config/AppConfigProvider";
import { UniversalCommandBar } from "../features/command-bar/UniversalCommandBar";
import { AccountMenu } from "../features/account/AccountMenu";

const { Header, Sider, Content }=Layout;

export function AppShell(){
  const {user}=useAuth(); const {text,locale,setLocale,theme,setTheme}=useAppConfig();
  const {md}=Grid.useBreakpoint(); const [open,setOpen]=useState(false); const location=useLocation();
  const can=(permission:PermissionCode)=>user?.permissions.includes(permission)??false;
  const isAdmin=Boolean(user?.roles.includes(SystemRole.Admin)&&can(PermissionCode.RoleManage));
  const labels=text as unknown as Record<string,string>;
  const workspaceMatch=location.pathname.match(/^\/repair-orders\/([^/]+)\/workspace(?:\/([^/]+))?/);
  const workspaceId=workspaceMatch?.[1];
  const requestedWorkspaceSection=workspaceMatch?.[2]??"overview";
  const legacyAnchor=requestedWorkspaceSection==="overview"?"concern"
    :["inspection"].includes(requestedWorkspaceSection)?"inspection"
    :["estimate"].includes(requestedWorkspaceSection)?"approval"
    :["labor","parts","photos","shop-work"].includes(requestedWorkspaceSection)?"work"
    :["invoice","payment"].includes(requestedWorkspaceSection)?"commercial-close":requestedWorkspaceSection;
  const activeWorkspaceSection=location.hash.replace("#","")||legacyAnchor;
  const workspaceSections=[
    {key:"concern",label:"visitReason",icon:<ProfileOutlined/>},
    {key:"inspection",label:"whatWeFound",icon:<SolutionOutlined/>},
    {key:"services",label:"whatWeRecommend",icon:<BuildOutlined/>},
    {key:"approval",label:"customerDecision",icon:<CheckCircleOutlined/>},
    {key:"work",label:"shopExecution",icon:<BuildOutlined/>},
    {key:"commercial-close",label:"commercialClose",icon:<CreditCardOutlined/>},
    {key:"timeline",label:"timeline",icon:<ClockCircleOutlined/>},
    {key:"history",label:"history",icon:<HistoryOutlined/>},
    {key:"activity-log",label:"activityLog",icon:<AuditOutlined/>}
  ];
  const workspaceItems=workspaceId?workspaceSections.map(item=>({
    key:`workspace-${item.key}`,
    icon:item.icon,
    className:"repair-workspace-sider-item",
    label:<Link to={`/repair-orders/${workspaceId}/workspace#${item.key}`}>{labels[item.label]}</Link>
  })):[];
  const items=[
    {key:"/",icon:<HomeOutlined/>,label:<Link to="/">{text.frontDesk}</Link>},
    ...(can(PermissionCode.RepairOrderRead)?[{key:"/repair-orders",icon:<FileTextOutlined/>,label:<Link to="/repair-orders">{text.repairOrders}</Link>}]:[]),
    ...(user?.roles.some(role=>[
      SystemRole.Admin,SystemRole.Manager,SystemRole.Technician,SystemRole.Warehouse
    ].includes(role as SystemRole))?[{key:"/workshop",icon:<ToolOutlined/>,label:<Link to="/workshop">{labels.technicianQueue}</Link>}]:[]),
    ...workspaceItems,
    ...(can(PermissionCode.InventoryRead)?[{
      key:"inventory-management",icon:<AppstoreOutlined/>,label:labels.inventoryManagement,
      children:[
        {key:"/inventory",label:<Link to="/inventory">{labels.inventoryOverview}</Link>},
        ...(can(PermissionCode.SupplierRead)?[{key:"/purchase",icon:<ShoppingCartOutlined/>,label:<Link to="/purchase">{text.purchase}</Link>}]:[])
      ]
    }]:[]),
    ...(can(PermissionCode.RepairOrderRead)&&can(PermissionCode.InvoiceRead)&&can(PermissionCode.PaymentRead)?[{key:"/reports",icon:<BarChartOutlined/>,label:<Link to="/reports">{text.reports}</Link>}]:[]),
    {
      key:"administration",icon:<SettingOutlined/>,label:text.administration,
      children:[
        ...(isAdmin?[{
          key:"access-management",icon:<TeamOutlined/>,label:text.usersAndPermissions,
          children:[
            {key:"/access/users",label:<Link to="/access/users">{text.userManagement}</Link>},
            ...(isAdmin?[{key:"/access/roles",label:<Link to="/access/roles">{text.roleManagement}</Link>}]:[])
          ]
        }]:[]),
        ...(can(PermissionCode.ActivityLogRead)?[
          {key:"/system/activity-logs",icon:<AuditOutlined/>,label:<Link to="/system/activity-logs">{text.activity}</Link>},
          {key:"/system/input-standards-test",icon:<ExperimentOutlined/>,label:<Link to="/system/input-standards-test">{text.inputStandardsTest}</Link>},
          {key:"/system/pilot-validation",icon:<SafetyCertificateOutlined/>,label:<Link to="/system/pilot-validation">Pilot Validation</Link>}
        ]:[])
      ]
    }
  ];
  const selectedKey=workspaceId
    ?`workspace-${activeWorkspaceSection}`
    :(["/repair-orders","/workshop","/inventory","/purchase","/reports","/access/users","/access/roles","/system/pilot-validation","/system/activity-logs","/system/input-standards-test"].find(key=>location.pathname.startsWith(key))??"/");
  const nav=<><div className="shell-logo"><span>AF</span><div><b>AutoFlow Pro</b><small>V7 PROFESSIONAL</small></div></div><Menu theme="dark" mode="inline" defaultOpenKeys={["inventory-management"]} selectedKeys={[selectedKey]} items={items} onClick={()=>setOpen(false)}/></>;
  return <Layout className="app-shell">
    {md?<Sider width={240} className="shell-sider">{nav}</Sider>:<Drawer open={open} onClose={()=>setOpen(false)} placement="left" width={280} styles={{body:{padding:0,background:"#111827"}}}>{nav}</Drawer>}
    <Layout>
      <Header className="shell-header">{!md&&<Button type="text" icon={<MenuOutlined/>} onClick={()=>setOpen(true)}/>}<UniversalCommandBar/><div className="header-spacer"/><Space>
        <Button type="text" icon={<GlobalOutlined/>} onClick={()=>setLocale(locale==="en"?"zh":"en")}>{locale==="en"?"中文":"EN"}</Button>
        <Switch checked={theme==="dark"} onChange={checked=>setTheme(checked?"dark":"light")} checkedChildren={<MoonOutlined/>} unCheckedChildren={<SunOutlined/>}/>
        <AccountMenu/>
      </Space></Header>
      <Content className="shell-content"><Outlet/></Content>
    </Layout>
  </Layout>;
}
